/**
 * PURPOSE: Componer o editar un borrador de Comunicado (título, mensaje, adjunto).
 * RESPONSIBILITIES: Aplicar los límites de negocio (título 120, mensaje 4000) como feedback de
 *   cliente — nunca como el límite real, el backend re-valida —, mostrar la Audiencia fija "Todos"
 *   sin selector editable (FTR-0005 §6), y ofrecer "Revisar y publicar"/"Ocultar" sólo en modo
 *   edición sobre un borrador ya guardado y sin cambios sin guardar.
 * COLLABORATORS: HayllisCommunicationAttachmentField.jsx, hayllisNotificationsService.js,
 *   HayllisCommunicationsPanel.jsx (crea/edita, luego delega revisión/publicación/ocultamiento).
 * ROUTE: winscore/src/admin/HayllisCommunications/HayllisCommunicationForm.jsx
 */
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import {
  createHayllisCommunicationDraft,
  getApiErrorMessage,
  updateHayllisCommunicationDraft,
} from "../../services/hayllisNotificationsService.js";
import HayllisCommunicationAttachmentField from "./HayllisCommunicationAttachmentField.jsx";

const MAX_TITLE_LENGTH = 120;
const MAX_BODY_LENGTH = 4000;

const sameAttachment = (a, b) => (a?.url || null) === (b?.url || null);

const HayllisCommunicationForm = ({ mode, initialData, onSaved, onRequestPublish, onRequestHide }) => {
  const [title, setTitle] = useState(initialData?.title || "");
  const [body, setBody] = useState(initialData?.body || "");
  const [attachment, setAttachment] = useState(initialData?.attachment || null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTitle(initialData?.title || "");
    setBody(initialData?.body || "");
    setAttachment(initialData?.attachment || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData?.id, mode]);

  const isDirty =
    mode === "edit" &&
    (title.trim() !== (initialData?.title || "") ||
      body.trim() !== (initialData?.body || "") ||
      !sameAttachment(attachment, initialData?.attachment));

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmedTitle = title.trim();
    const trimmedBody = body.trim();
    if (!trimmedTitle || trimmedTitle.length > MAX_TITLE_LENGTH) {
      toast.error(`El título es obligatorio (máx. ${MAX_TITLE_LENGTH} caracteres).`);
      return;
    }
    if (!trimmedBody || trimmedBody.length > MAX_BODY_LENGTH) {
      toast.error(`El mensaje es obligatorio (máx. ${MAX_BODY_LENGTH} caracteres).`);
      return;
    }

    setSaving(true);
    try {
      const payload = { title: trimmedTitle, body: trimmedBody, attachment: attachment ?? null };
      const notification =
        mode === "edit"
          ? await updateHayllisCommunicationDraft(initialData.id, payload)
          : await createHayllisCommunicationDraft(payload);
      toast.success(mode === "edit" ? "Comunicado actualizado." : "Borrador creado.");
      onSaved(notification);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudo guardar el comunicado."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
        Título *
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={MAX_TITLE_LENGTH}
          required
          disabled={saving}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-slate-100 dark:border-slate-600 dark:bg-gray-800 dark:text-white"
        />
        <span className="mt-1 block text-right text-xs font-normal text-slate-400">
          {title.length}/{MAX_TITLE_LENGTH}
        </span>
      </label>

      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
        Mensaje *
        <textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          maxLength={MAX_BODY_LENGTH}
          required
          rows={6}
          disabled={saving}
          className="mt-1 w-full resize-y rounded-lg border border-slate-300 px-3 py-2 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:bg-slate-100 dark:border-slate-600 dark:bg-gray-800 dark:text-white"
        />
        <span className="mt-1 block text-right text-xs font-normal text-slate-400">
          {body.length}/{MAX_BODY_LENGTH}
        </span>
      </label>

      <HayllisCommunicationAttachmentField value={attachment} onChange={setAttachment} disabled={saving} />

      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm dark:border-slate-600 dark:bg-gray-800">
        <span className="font-semibold text-slate-600 dark:text-slate-300">Audiencia: </span>
        <span className="text-slate-700 dark:text-white">Todos</span>
      </div>

      <div className="flex flex-wrap justify-end gap-2 pt-2">
        {mode === "edit" && (
          <button
            type="button"
            onClick={() => onRequestHide(initialData)}
            disabled={saving}
            className="rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Ocultar borrador
          </button>
        )}
        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? "Guardando..." : mode === "edit" ? "Guardar cambios" : "Crear borrador"}
        </button>
        {mode === "edit" && (
          <button
            type="button"
            onClick={() => onRequestPublish(initialData)}
            disabled={saving || isDirty}
            title={isDirty ? "Guarda los cambios antes de publicar." : undefined}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Revisar y publicar
          </button>
        )}
      </div>
      {mode === "edit" && isDirty && (
        <p className="text-right text-xs font-semibold text-amber-600">
          Tienes cambios sin guardar. Guarda antes de publicar.
        </p>
      )}
    </form>
  );
};

export default HayllisCommunicationForm;
